<template>
  <div>
    <aw-full-tiles
      v-if="!verticalOnly"
      :content="displayTiles"
      background-color="colorBackground2"
      :analytics-banner-interaction="analyticsBannerInteraction"
      :analytics-measurable="analyticsMeasurable"
    />
    <aw-vertical-tiles
      v-else
      :content="displayTiles"
      background-color="colorBackground2"
      :is-mobile-carousel="false"
      :analytics-banner-interaction="analyticsBannerInteraction"
      :analytics-measurable="analyticsMeasurable"
    />
    <aw-spinner v-if="isLoading" color="currentColor" :class="$style.loadMoreSpinner" />
    <div v-else-if="displayTiles.components.length" :class="$style.loadMore">
      <lv-button v-if="displayLoadMore" styling="secondary-dark" @click="loadMorePage">
        {{ $awt('aw.pages.aggregated_common.load_more') }}
        <lv-icon name="arrow-down-16" :size="16" />
      </lv-button>
    </div>
    <aw-empty v-else>
      {{ $awt('aw.pages.aggregated_common.no_results') }}
    </aw-empty>
  </div>
</template>

<script setup>
  import { computed, defineAsyncComponent, markRaw, shallowRef } from 'vue';
  import { useNuxtApp, useAsyncData } from 'nuxt/app';
  import { AW_AX_CACHE_GLOBAL } from '~~/common/plugins/axios/awAxCache';
  import { setErrorNotification } from '~~/common/utils/helper';
  import { useUserInterfaceStore } from '~~/common/stores/userInterface.js';
  import { getBackgroundImages } from '~~/common/utils/imageHelper.js';
  import otqs from '~~/common/utils/objectToQueryString';
  import analyticsBannerInteractionMixin from '~~/common/mixins/analyticsBannerInteractionMixin.js';
  import { LvIcon, LvButton } from '~~/common/components/loginet-vue-shop/index';
  import AwSpinner from '~~/common/components/Common/AwSpinner';
  import AwVerticalTiles from '~~/common/components/AwStandardPartials/AwVerticalTiles.vue';

  const AwEmpty = defineAsyncComponent(() => import('~~/common/components/Common/AwEmpty'));
  const AwFullTiles = defineAsyncComponent(() => import('~~/common/components/AwStandardPartials/AwFullTiles'));

  const props = defineProps({
    ...analyticsBannerInteractionMixin.props,
    pageType: {
      type: String,
      required: true,
      validator: p => ['press_release', 'news', 'csr', 'inspiration', 'career_news'].includes(p),
    },
    params: {
      type: Object,
      required: true,
    },
    hideLoadMoreButton: {
      type: Boolean,
      required: false,
    },
    verticalOnly: {
      type: Boolean,
      default: false,
    },
  });
  const nuxtApp = useNuxtApp();
  const {
    $api,
    $awt,
    $logger,
    $styleVariables,
  } = nuxtApp;
  const isLoading = ref(false);
  const paginationParams = ref({
    page: 1,
    limit: 12,
  });
  const mediaQueries = useUserInterfaceStore().useMediaQueries();
  const dataKey = computed(() => {
    const params = {
      highlighted: false,
      ...paginationParams.value,
      ...props.params,
    };
    return `cms/tag-groups/${props.pageType}${otqs(params)}`;
  });
  const apiData = shallowRef(createEmptyApiData());
  const asyncDataResult = await useAsyncData(
    dataKey.value,
    async () => {
      return markRaw(await fetchData());
    },
    {
      watch: [
        dataKey,
      ],
      default: createEmptyApiData,
    },
  );
  watch(
    () => asyncDataResult.data.value,
    (newVal, oldVal) => {
      if (newVal !== oldVal) {
        apiData.value = newVal;
      }
    },
    {
      immediate: true,
    },
  );
  const displayLoadMore = computed(() => {
    return apiData.value.paginationData.pageCount > paginationParams.value.page && !props.hideLoadMoreButton;
  });
  const displayTiles = computed(() => {
    const tileData = apiData.value.data.map((tile, index) => {
      const desktopCardImage = tile.media?.desktopCardImage;
      const mobileCardImage = tile.media?.mobileCardImage;
      const images = [
        {
          ...desktopCardImage,
          media: desktopCardImage && mobileCardImage ? `(min-width: ${$styleVariables.breakpointTablet})` : undefined,
        },
        {
          ...mobileCardImage,
          media: desktopCardImage && mobileCardImage ? `(max-width: ${$styleVariables.breakpointTablet})` : undefined,
        },
      ];

      const contentImage = getBackgroundImages(mediaQueries.value['tablet-min'] ? images : images.reverse())[0];

      return {
        id: index,
        type: 'standard',
        layoutTemplate: 'tiles',
        title: tile.title,
        campaignName: tile.title,
        contentText: tile?.lead,
        dateTag: tile.publishedAt,
        showPlaceholder: true,
        media: {
          contentImage,
        },
        // mobileCardImage
        button: {
          alignment: 'center',
          items: [
            {
              text: $awt('aw.pages.aggregated_common.tile.button_title'),
              link: tile?.url,
              type: 'small-secondary-dark',
              mobileType: 'small-secondary-dark',
            },
          ],
        },
        contentBackgroundColorOnly: false,
        horizontalLayout: false,
        tags: ['csr', 'inspiration'].includes(props.pageType) ? tile.tags : [],
      };
    });
    return {
      id: 0,
      layoutType: 'full_tiles',
      spacingType: 'positive',
      fullWidth: false,
      measurablePromotion: false,
      components: tileData,
    };
  });

  function loadMorePage () {
    paginationParams.value.page += 1;
  }

  async function fetchData () {
    try {
      isLoading.value = true;
      const result = await $api.$get(dataKey.value, { awAxCache: AW_AX_CACHE_GLOBAL });
      return {
        data: [
          ...apiData.value.data,
          ...(Array.isArray(result?.contentVersions) ? result.contentVersions : []),
        ],
        paginationData: {
          itemCount: result?.itemCount,
          pageCount: result?.pageCount,
        },
      };
    } catch (error) {
      $logger.error(error);
      setErrorNotification({
        nuxtApp,
        error,
      });
      return createEmptyApiData();
    } finally {
      isLoading.value = false;
    }
  }

  function createEmptyApiData () {
    return {
      data: [],
      paginationData: {
        itemCount: 0,
        pageCount: 0,
      },
    };
  }
</script>

<style module lang="scss" rel="stylesheet/scss">
.loadMore {
  display: flex;
  justify-content: center;
  width: 100%;
  margin-top: 20px;

  &Spinner {
    margin-top: 20px;
    color: $color-text-primary;
    @include tablet(min) {
      margin-top: 40px;
    }
  }

  @include tablet(min) {
    margin-top: 40px;
  }
}
</style>
